import { useTranslation } from 'react-i18next';
import { Inbox, SearchX, TriangleAlert } from 'lucide-react';
import axios from 'axios';
import { Button, EmptyState, Skeleton } from '@/ui';
import styles from './ListStates.module.css';

/**
 * LISTENIN UC DURUMU: yukleniyor / bos / hata.
 *
 * Ayri ayri yazilsalardi her listede biraz farkli gorunurlerdi; ayni durumun
 * ekrandan ekrana degismesi kullaniciya "baska bir sey oldu" hissi verir.
 *
 * BOS ile SONUC YOK ayri: filtre aciksa "henuz kayit yok" YANILTICIDIR — bu
 * kullanicinin kendi filtresinin sonucu ve DUZELTILEBILIR bir durum, o yuzden
 * cikis yolu (filtreleri temizle) gosterilir.
 */

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  const { t } = useTranslation();
  return (
    <div className={styles.skeleton} aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{t('common.loading')}</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} shape="block" height="132px" />
      ))}
    </div>
  );
}

export function ListEmpty({
  onClearFilters,
  description,
}: {
  onClearFilters?: (() => void) | undefined;
  /** Bos listenin nedeni (cevrilmis). Filtre kaynakli bos listede KULLANILMAZ. */
  description?: string | undefined;
}) {
  const { t } = useTranslation();

  if (onClearFilters) {
    return (
      <EmptyState
        icon={<SearchX size={28} aria-hidden="true" />}
        title={t('list.noResults.title')}
        description={t('list.noResults.description')}
        action={
          <Button tone="neutral" onClick={onClearFilters}>
            {t('list.noResults.action')}
          </Button>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<Inbox size={28} aria-hidden="true" />}
      title={t('list.empty.title')}
      {...(description === undefined ? {} : { description })}
    />
  );
}

/**
 * Ag hatasi ile sunucu hatasi AYRI mesaj alir: "internetinizi kontrol edin"
 * cevaplanabilir bir yonlendirme, "bir sorun olustu" degil.
 */
const isNetworkError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response === undefined;

export function ListError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      tone="error"
      icon={<TriangleAlert size={28} aria-hidden="true" />}
      title={t('list.error.title')}
      description={isNetworkError(error) ? t('list.error.network') : t('list.error.generic')}
      action={
        <Button tone="neutral" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      }
    />
  );
}

/**
 * SAYFA GEZINTISI.
 *
 * "3 / 17" YOK ve olamaz: sunucu sayfalama ust bilgisinde `total` DONDURMUYOR
 * (bkz. src/lib/task-server-list.ts). Toplami `returned < limit` gibi bir
 * kestirimle uydurmak, tam dolu son sayfada yanlis olur — soylemedigimiz bir
 * sey, yanlis soyledigimiz bir seyden iyidir. Onun yerine kacinci kaydin
 * gosterildigi yaziliyor.
 */
export type PagerProps = {
  offset: number;
  pageSize: number;
  returned: number;
  hasMore: boolean;
  busy: boolean;
  onOffsetChange: (offset: number) => void;
};

export function Pager({ offset, pageSize, returned, hasMore, busy, onOffsetChange }: PagerProps) {
  const { t } = useTranslation();
  if (offset === 0 && !hasMore) return null;

  const first = returned === 0 ? 0 : offset + 1;
  const last = offset + returned;

  return (
    <nav className={styles.pager} aria-label={t('task.pagination')}>
      <Button
        tone="neutral"
        size="sm"
        disabled={offset === 0 || busy}
        onClick={() => {
          onOffsetChange(Math.max(0, offset - pageSize));
        }}
      >
        {t('task.previousPage')}
      </Button>
      <span className={styles.pagerRange}>
        {t('task.rangeLabel', { first, last })}
      </span>
      <Button
        tone="neutral"
        size="sm"
        disabled={!hasMore || busy}
        onClick={() => {
          onOffsetChange(offset + pageSize);
        }}
      >
        {t('task.nextPage')}
      </Button>
    </nav>
  );
}
